import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { TableRow } from "@src/pages/super-task-manager/creation-drawers/CreateListDrawer";

export type APIResponse = {
  httpStatus: string;
  statusCode: number;
};

export type AddChallengeApiResponse = APIResponse;
export type UpdateChallengeApiResponse = APIResponse;

const BASE_URL: string = import.meta.env.VITE_TEST_FRESHERS_CHALLENGE_BASE_URL;

interface QuizRunPayload {
  userId: string;
  challengeId: number;
  language: string;
  userCode: string;
  status: string;
  questionProgramType: "UNORDER" | "ORDER" | string;
  difficultyLevel: "EASY" | "MEDIUM" | "HARD" | string;
}

interface RunUserCodeResponse {
  statusCode: number;
  httpStatus: string;
  response: {
    id: number;
    userId: string;
    questionProgramType: "UNORDER" | "ORDER" | string;
    status: string;
    difficultyLevel: "EASY" | "MEDIUM" | "HARD" | string;
    userSubmissionLogs: {
      id: number;
      status: string;
      duration: string;
      language: string;
      challengeSolutionResponse: string;
    }[];
  };
}

export interface UploadChallengeApiResponse extends APIResponse {
  response: string;
}

export const ChallengeApiSlice = createApi({
  reducerPath: "challengeApi",
  baseQuery: fetchBaseQuery({ baseUrl: BASE_URL }),
  endpoints: (builder) => ({
    getAllChalenges: builder.query<
      {
        statusCode: number;
        httpStatus: string;
        response: { content: TableRow[] };
      },
      { categoryId: string; pageNo: number; pageSize: number }
    >({
      query: ({ categoryId, pageNo, pageSize }) => ({
        url: "challenge/get-all",
        method: "GET",
        params: { categoryId, pageNo, pageSize },
      }),
    }),
    addChallenge: builder.mutation<
      AddChallengeApiResponse,
      { payload: FormData; categoryId: string }
    >({
      query: ({ payload, categoryId }) => ({
        url: "challenge",
        method: "POST",
        body: payload,
        params: { categoryId },
      }),
    }),
    updateChallenge: builder.mutation<
      UpdateChallengeApiResponse,
      { payload: FormData; categoryId: string }
    >({
      query: ({ payload, categoryId }) => ({
        url: `challenge`,
        method: "PUT",
        body: payload,
        params: { categoryId },
      }),
    }),
    runFrontendChallengeCode: builder.mutation<
      RunUserCodeResponse,
      QuizRunPayload
    >({
      query: (payload) => ({
        url: "challenge-submission/challenge-solution-submissions",
        method: "POST",
        body: payload,
        headers: {
          "Content-Type": "application/json",
        },
      }),
    }),
    uploadChallenges: builder.mutation<
      UploadChallengeApiResponse,
      { file: FormData; categoryId: string; uploadWithRun: boolean }
    >({
      query: ({ file, categoryId, uploadWithRun }) => {
        let URL = `challenge/bulkUploadXls/${categoryId}`;
        if (uploadWithRun) URL = `challenge/uploadProgrammingXls/${categoryId}`;
        return {
          url: URL,
          method: "POST",
          body: file,
        };
      },
    }),
  }),
});

export const {
  useGetAllChalengesQuery,
  useLazyGetAllChalengesQuery,
  useRunFrontendChallengeCodeMutation,
  useAddChallengeMutation,
  useUpdateChallengeMutation,
  useUploadChallengesMutation,
} = ChallengeApiSlice;
